/**
 * Per-plan pricing layout on skillsnap.me/pricing.html.
 *
 * MUST agree with `planLayout` in website/landing/assets/pricing-render.js (the
 * landing renderer — the actual source of truth for what the public page does);
 * src/__tests__/pricingLayout.test.ts loads that file and asserts parity.
 *
 *   'tile'       -> a card in the horizontal row of pricing tiles
 *   'full_width' -> a full-width row rendered below the tiles
 *
 * A plan saved before the field existed has no `layout`; it is derived exactly
 * as the landing page always decided it: key 'enterprise' -> full-width, every
 * other plan -> tile.
 */
import type { PricingConfig, PricingLayout, PricingPlan } from '../services/api';

export const PRICING_LAYOUTS: readonly PricingLayout[] = ['tile', 'full_width'];

export const PRICING_LAYOUT_LABELS: Record<PricingLayout, string> = {
  tile: 'Horizontal tile',
  full_width: 'Full-width row (below tiles)',
};

export function isPricingLayout(v: unknown): v is PricingLayout {
  return v === 'tile' || v === 'full_width';
}

export function planLayout(key: string, plan: Pick<PricingPlan, 'layout'> | null | undefined): PricingLayout {
  const v = plan?.layout;
  if (isPricingLayout(v)) return v;
  return key === 'enterprise' ? 'full_width' : 'tile';
}

/** True when the plan has no explicit layout and the value shown is derived. */
export function isLayoutDerived(plan: Pick<PricingPlan, 'layout'> | null | undefined): boolean {
  return !isPricingLayout(plan?.layout);
}

/**
 * Origin of the landing site whose real pricing page the admin preview frames.
 * Deployment config (not sourced data): override with VITE_LANDING_URL to point
 * a local admin at a locally served landing site.
 */
export const LANDING_URL: string = (import.meta.env.VITE_LANDING_URL || 'https://skillsnap.me').replace(/\/+$/, '');

export function pricingPreviewUrl(landingUrl: string = LANDING_URL): string {
  return `${landingUrl}/pricing.html?preview=1`;
}

/**
 * The config exactly as the public GET /pricing returns it
 * (admin/pricing_public.py strips `stripe_price_id` from each plan), so the
 * preview receives the same shape the live page renders.
 */
export function toPublicPricing(pricing: PricingConfig): PricingConfig {
  const plans: Record<string, PricingPlan> = {};
  for (const [key, plan] of Object.entries(pricing.plans || {})) {
    const { stripe_price_id: _omit, ...rest } = plan;
    plans[key] = rest as PricingPlan;
  }
  return { ...pricing, plans };
}
